import { useState, useEffect, lazy, Suspense } from "react";
import { Panel, Nav } from "rsuite";
import { Box } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { FaMousePointer } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import LoaderBox from "../components/LoaderBox";
import "./InformationPanel.css";

const DeviceInformation = lazy(() => import("../components/DeviceInformation"));
const ActionsPanel = lazy(() => import("../components/ActionsPanel"));

const contentVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 260, damping: 26 },
  },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15 } },
};

const InformationPanel = ({ device, deviceData }) => {
  const [t] = useTranslation();
  const defaultNavItems = [
    {
      eventKey: "information",
      label: t("information-panel.information"),
    },
    {
      eventKey: "actions",
      label: t("information-panel.actions"),
    },
    {
      eventKey: "future",
      label: t("information-panel.future"),
    },
  ];
  const defaultNavItem = defaultNavItems[0].eventKey;
  const [active, setActive] = useState(defaultNavItem);

  useEffect(() => {
    if (device) setActive(defaultNavItem);
  }, [device, defaultNavItem]);

  return (
    <Panel className="content p-0 content-final dashboard-card">
      <Nav appearance="subtle" active={active} onSelect={setActive}>
        {defaultNavItems.map((navItem) => {
          const { eventKey, label } = navItem;
          return (
            <Nav.Item
              eventKey={eventKey}
              active={active === eventKey ? true : false}
              key={eventKey}
            >
              {label}
            </Nav.Item>
          );
        })}
      </Nav>
      <AnimatePresence mode="wait">
        {!device && (
          <motion.div
            key="empty"
            className="info-panel-empty"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <span className="info-panel-empty__icon">
              <FaMousePointer />
            </span>
            <span className="info-panel-empty__text">
              {t("information-panel.select-device")}
            </span>
          </motion.div>
        )}
        {active === defaultNavItems[0].eventKey && device && (
          <motion.div
            key={`info-${device}`}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box p={4} bg="transparent">
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <DeviceInformation deviceId={device} />
              </Suspense>
            </Box>
          </motion.div>
        )}
        {active === defaultNavItems[1].eventKey && device && (
          <motion.div
            key={`actions-${device}`}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box p={4} bg="transparent">
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <ActionsPanel deviceData={deviceData} />
              </Suspense>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Panel>
  );
};

export default InformationPanel;
